import { bisector, extent } from 'd3-array';
import { Axis, axisBottom, axisRight } from 'd3-axis';
import { format } from 'd3-format';
import { ScaleLinear, ScaleTime, scaleLinear, scaleTime } from 'd3-scale';
import { Selection, mouse, select, selectAll } from 'd3-selection';
import { Area, CurveFactory, Line, area, curveBasis, line } from 'd3-shape';
import { timeFormat } from 'd3-time-format';

import { parseInteger } from '../models/helpers';

interface DataPoint {
  [key: string]: Date | number;
  date: Date;
  expected: number;
  ninetyPercentile: number;
  tenPercentile: number;
}

export interface ReturnsGraphSettings {
  margin: {
    top: number;
    right: number;
    left: number;
    bottom: number;
  };
  width: number;
  height: number;
  aspectRatio: number;
  tooltipClass: string;
  displayYAxis: boolean;
  interpolation: CurveFactory;
  element: SVGElement;
  minValue: number;
  maxValue: number;
  valuesByDate: Array<DataPoint>;
  termYears: number;
}

export type ReturnsGraphDefaults = Pick<
  ReturnsGraphSettings,
  'margin' | 'aspectRatio' | 'tooltipClass' | 'displayYAxis' | 'interpolation'
>;

export type ReturnsGraphOptions = Partial<ReturnsGraphDefaults> &
  Omit<ReturnsGraphSettings, 'element' | 'width' | 'height' | keyof ReturnsGraphDefaults>;

interface ReturnsGraphGenerators {
  xAxis: Axis<Date>;
  yAxis: Axis<number>;
  line: {
    [key: string]: Line<DataPoint>;
    expected: Line<DataPoint>;
    zero: Line<DataPoint>;
  };
  area: {
    [key: string]: Area<DataPoint>;
    midRange: Area<DataPoint>;
  };
}

interface Scales {
  x: ScaleTime<number, number>;
  y: ScaleLinear<number, number>;
}

export default class ReturnsGraph {
  settings: ReturnsGraphSettings;

  svg: Selection<SVGGElement, DataPoint, HTMLElement, unknown>;

  scales: Scales;

  generators: ReturnsGraphGenerators;

  constructor(element: ReturnsGraphSettings['element'], options: ReturnsGraphOptions) {
    const rightMargin = (): number => {
      if (options.displayYAxis) return options.termYears > 20 ? 100 : 80;
      return options.termYears < 10 ? 60 : 40;
    };

    const DEFAULTS: ReturnsGraphDefaults = {
      margin: {
        top: 20,
        right: rightMargin(),
        left: 20,
        bottom: 30,
      },
      aspectRatio: 16 / 9,
      tooltipClass: '.ProjectionGraph-tooltip',
      displayYAxis: false,
      interpolation: curveBasis,
    };

    this.settings = { ...DEFAULTS, ...options, element, width: 0, height: 0 };
    const elWidth = this.settings.element.parentElement!.clientWidth;
    this.settings.width = elWidth - this.settings.margin.right;
    this.settings.height =
      Math.ceil(elWidth / this.settings.aspectRatio) - this.settings.margin.top - this.settings.margin.bottom;
  }

  draw() {
    this.emptyLayout();
    this.setUpScales();
    this.setUpGenerators();
    this.createLayout();
    this.drawData();
    this.drawHoverBar();
    this.initHover();
  }

  emptyLayout() {
    const svg = this.settings.element;
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }
  }

  setUpScales() {
    const yMin = this.settings.minValue;
    const yMax = this.settings.maxValue;

    this.scales = {
      x: scaleTime()
        .domain(
          extent<DataPoint, Date>(this.settings.valuesByDate, d => {
            return d.date;
          }) as [Date, Date]
        )
        .range([0, this.settings.width]),
      y: scaleLinear().domain([yMin, yMax]).range([this.settings.height, 0]).nice(),
    };
  }

  setUpGenerators() {
    const { scales } = this;
    const { interpolation } = this.settings;
    const formatDate = timeFormat(this.settings.termYears > 10 ? '%Y' : '%b %Y');
    const axisDateFormat = (d: Date) => (d.getTime() === scales.x.domain()[0].getTime() ? 'Today' : formatDate(d));
    const yTicks = this.settings.width < 500 ? 4 : 8;

    this.generators = {
      xAxis: axisBottom<Date>(scales.x)
        .tickValues(scales.x.domain())
        .tickSize(0)
        .ticks(2)
        .tickPadding(10)
        .tickFormat(axisDateFormat),
      yAxis: axisRight<number>(scales.y)
        .tickSize(10)
        .ticks(yTicks)
        .tickPadding(10)
        .tickFormat(d => {
          return `${d}%`;
        }),
      line: {
        expected: line<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y(d => scales.y(d.expected) as number)
          .curve(interpolation),
        zero: line<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y(scales.y(0) as number),
      },
      area: {
        midRange: area<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y0(d => scales.y(d.tenPercentile) as number)
          .y1(d => scales.y(d.ninetyPercentile) as number)
          .curve(interpolation),
      },
    };
  }

  createLayout() {
    this.svg = select<SVGGElement, DataPoint>('svg.ProjectionGraph-svg')
      .attr('width', this.settings.width + this.settings.margin.left + this.settings.margin.right)
      .attr('height', this.settings.height + this.settings.margin.top + this.settings.margin.bottom)
      .on('mousemove', () => {
        // @ts-ignore We need to confirm how this graph can be manually tested before changing this to this.svg.node()
        this.updateHover(this.settings.element);
      })
      .on('mouseout', () => {
        this.hideHover();
      })
      .append('g')
      .attr('transform', `translate(${this.settings.margin.left},${this.settings.margin.top})`);

    this.svg
      .append('g')
      .attr('class', 'ProjectionGraph-axis')
      .attr('transform', `translate(0,${this.scales.y(this.settings.minValue) || ''})`)
      .call(this.generators.xAxis);
    this.svg.selectAll('path.domain').style('visibility', 'hidden');

    if (this.settings.displayYAxis) {
      this.svg
        .append('g')
        .attr('class', 'ProjectionGraph-axis--y ProjectionGraph-axis')
        .attr('transform', `translate(${this.settings.width} , 0)`)
        .call(this.generators.yAxis);
    }

    this.svg.selectAll('g.tick').attr('class', 'ProjectionGraph-axisTick ProjectionGraph-label');
  }

  drawData() {
    this.drawArea('midRange');

    this.drawLine('expected');
    this.drawLine('zero');

    this.drawCircle('ninetyPercentile');
    this.drawCircle('tenPercentile');
    this.drawCircle('expected');
  }

  drawArea(type: string) {
    this.svg
      .append('path')
      .datum(this.settings.valuesByDate)
      .attr('class', `ProjectionGraph-area ProjectionGraph-area--${type}`)
      .attr('d', this.generators.area[type]);
  }

  drawLine(type: string) {
    this.svg
      .append('path')
      .datum(this.settings.valuesByDate)
      .attr('class', `ProjectionGraph-line ProjectionGraph-line--${type}`)
      .attr('d', this.generators.line[type]);
  }

  drawCircle(type: string) {
    this.svg
      .append('circle')
      .attr('r', 3)
      .attr('class', `ProjectionGraph-hover ProjectionGraph-circle ProjectionGraph-circle--${type}`)
      .attr('data-type', type);
  }

  drawHoverBar() {
    this.svg.append('path').attr('class', 'ProjectionGraph-hover ProjectionGraph-bar');
  }

  initHover() {
    const lastPoint = this.settings.valuesByDate[this.settings.valuesByDate.length - 1];
    this.updateHoverBar(lastPoint);
    this.updateTooltip({ point: lastPoint, mouseX: 0 });
    this.updateCircles(lastPoint);
    this.hideHover();
  }

  updateHoverBar(point: DataPoint) {
    const x = this.scales.x(new Date(point.date));
    select('.ProjectionGraph-hover.ProjectionGraph-bar')
      .classed('ProjectionGraph-hover--show', true)
      .attr('d', `M${x || ''},${this.settings.height || ''}L${x || ''},${0}`);
  }

  updateTooltip(options: { point: DataPoint; mouseX: number }) {
    const tooltip = document.querySelector(this.settings.tooltipClass) as HTMLElement;
    const { point } = options;
    const toolWidth = tooltip.offsetWidth;
    const offset = options.mouseX - toolWidth;
    const tooltipYPosition = this.settings.element.parentElement!.offsetTop;

    (tooltip.querySelector('.ProjectionGraph-tooltipDate') as HTMLElement).innerText = timeFormat('%B %Y')(
      // @ts-ignore We need a way to manually test this before getting rid of the parseInteger to match the DataPoint interface
      new Date(parseInteger(point.date))
    );

    Array.from(tooltip.querySelectorAll('.ProjectionGraph-tooltipValue')).forEach(item => {
      const type = item.getAttribute('data-value-type');
      const f = format('+');
      item.innerHTML =
        // @ts-ignore We need a way to manually test this before getting rid of the parseInteger to match the DataPoint interface
        parseFloat(point[type!]) === 0 ? `${parseFloat(point[type!])}%` : `${f(parseFloat(point[type!]))}%`;
    });

    function updateTooltipBoxPadding(scales: Scales) {
      const leftValue = scales.x(point.date) || 0 + 31 - (offset > 0 ? toolWidth - 2 : 1);
      tooltip.style.left = `${leftValue}px`;
      tooltip.style.top = `${tooltipYPosition}px`;
      tooltip.classList.add('ProjectionGraph-hover--show');
    }

    updateTooltipBoxPadding(this.scales);
  }

  updateCircles(point: DataPoint) {
    selectAll('.ProjectionGraph-circle')
      .classed('ProjectionGraph-hover--show', true)
      .attr('cx', this.scales.x(point.date) || 0);
    Array.from(document.querySelectorAll('.ProjectionGraph-circle')).forEach(circle => {
      const type = circle.getAttribute('data-type')!;
      circle.setAttribute('cy', `${this.scales.y(point[type]) || 0}`);
    });
  }

  updateHover(el: SVGGElement) {
    const container = el;
    const mouseX = mouse(container)[0] - this.settings.margin.left;
    const mouseY = mouse(container)[1];

    if (mouseX >= 0 && mouseX <= this.settings.width && mouseY >= 0 && mouseY <= this.settings.height) {
      const exactDate = this.scales.x.invert(mouseX);
      const point = this.getClosestDataPoint(exactDate);

      this.updateHoverBar(point);
      this.updateTooltip({
        point,
        mouseX,
      });
      this.updateCircles(point);
    } else {
      this.hideHover();
    }
  }

  getClosestDataPoint(date: Date) {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const bisect = bisector<DataPoint, {}>(d => d.date).right;
    const index = Math.min(bisect(this.settings.valuesByDate, date), this.settings.valuesByDate.length - 1);

    return this.settings.valuesByDate[index];
  }

  hideHover() {
    selectAll('.ProjectionGraph-hover').classed('ProjectionGraph-hover--show', false);
  }

  dateFormat(date: Date) {
    return timeFormat('%Y')(date);
  }
}
